import React, { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import type { AseguradoraDTO, CreateAseguradoraRequest, Status, WrapperResponse } from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import { clip } from '../../../utils/inputs'
import { errorMessage, INSURERS_PATH } from '../common/insurer-common'
import type { FormMessage, InsurersLocationState } from '../common/insurer-common'

export interface InsurerFormState {
    id: number | null
    aseguradora: string
    abreviacion: string
    status: Status
}

const LOAD_ERROR = 'Error al cargar la página'
const SAVE_ERROR = 'Error al guardar la aseguradora'
const DELETE_ERROR = 'Error al eliminar la aseguradora'
const NOT_FOUND = 'No se encontró la aseguradora.'

const emptyState: InsurerFormState = {
    id: null,
    aseguradora: '',
    abreviacion: '',
    status: 'activo',
}

const toFormState = (body: AseguradoraDTO): InsurerFormState => ({
    id: body.id,
    aseguradora: body.aseguradora || '',
    abreviacion: body.abreviacion || '',
    status: body.status === 'inactivo' ? 'inactivo' : 'activo',
})

const toRequest = (state: InsurerFormState): CreateAseguradoraRequest => ({
    id: state.id,
    aseguradora: state.aseguradora.trim(),
    abreviacion: state.abreviacion.trim(),
    status: state.status,
})

export const useInsurerFormModel = () => {
    const history = useHistory()
    const location = useLocation()
    const { aseguradoraId } = useParams()
    const backUrl: string = (location.state as InsurersLocationState | null)?.backUrl || INSURERS_PATH

    const [loading, setLoading] = useState(Boolean(aseguradoraId))
    const [saving, setSaving] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [message, setMessage] = useState<FormMessage | null>(null)
    const [standard, setStandard] = useState(false)
    const [state, setState] = useState<InsurerFormState>(emptyState)

    useEffect(() => {
        if (!aseguradoraId) {
            setState(emptyState)
            setStandard(false)
            setLoading(false)
            return undefined
        }
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                const response: WrapperResponse<AseguradoraDTO | null> | null = await (await APIInvoke.invokeGET(`/aseguradoras/${aseguradoraId}`)).json()
                if (!active) return
                if (!response?.ok) throw new Error(response?.message || LOAD_ERROR)
                if (!response.body) throw new Error(NOT_FOUND)
                setStandard(response.body.estandar === true)
                setState(toFormState(response.body))
            } catch (error) {
                console.error('Error al inicializar la página', error)
                if (active) setMessage({ type: 'error', message: errorMessage(error, LOAD_ERROR) })
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [aseguradoraId])

    const patch = (partial: Partial<InsurerFormState>) => setState((current) => ({ ...current, ...partial }))

    const change = {
        aseguradora: (value: string) => patch({ aseguradora: clip(value, 100) }),
        abreviacion: (value: string) => patch({ abreviacion: clip(value, 10) }),
        status: (value: Status) => patch({ status: value }),
    }

    const save = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (standard) return
        try {
            setSaving(true)
            setMessage(null)
            const request = toRequest(state)
            const response: WrapperResponse<AseguradoraDTO> = state.id
                ? await (await APIInvoke.invokePUT(`/aseguradoras/${state.id}`, request)).json()
                : await (await APIInvoke.invokePOST('/aseguradoras', request)).json()
            if (!response.ok) {
                setMessage({ type: 'error', message: response.message || SAVE_ERROR })
                return
            }
            history.push({ pathname: INSURERS_PATH, state: { flash: 'Aseguradora guardada correctamente' } satisfies InsurersLocationState })
        } catch (error) {
            console.error('Error al guardar', error)
            setMessage({ type: 'error', message: SAVE_ERROR })
        } finally {
            setSaving(false)
        }
    }

    const deleteInsurer = async () => {
        if (!state.id || deleting || standard) return
        try {
            setConfirmDelete(false)
            setDeleting(true)
            setMessage(null)
            const response: WrapperResponse<null> = await (await APIInvoke.invokeDELETE(`/aseguradoras/${state.id}`)).json()
            if (!response.ok) {
                setMessage({ type: 'error', message: response.message || DELETE_ERROR })
                return
            }
            history.push({ pathname: INSURERS_PATH, state: { flash: 'Aseguradora eliminada correctamente' } satisfies InsurersLocationState })
        } catch (error) {
            console.error('Error al eliminar la aseguradora', error)
            setMessage({ type: 'error', message: DELETE_ERROR })
        } finally {
            setDeleting(false)
        }
    }

    return {
        form: state,
        loading,
        saving,
        deleting,
        busy: loading || saving || deleting,
        busyLabel: deleting ? 'Eliminando aseguradora…' : loading ? 'Cargando aseguradora…' : 'Guardando aseguradora…',
        message,
        standard,
        isEdit: Boolean(state.id),
        title: state.id ? (standard ? 'Aseguradora predefinida' : 'Editar aseguradora') : 'Nueva aseguradora',
        backTo: backUrl,
        confirmDelete,
        change,
        save,
        cancel: () => history.push(backUrl),
        deleteInsurer,
        askDelete: () => setConfirmDelete(true),
        cancelDelete: () => setConfirmDelete(false),
    }
}
